import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";

function parseAgent(ua = "") {
  const browser = (ua.match(/(Edg|Chrome|Firefox|Safari|OPR)\/[\d.]+/) || [])[0]?.replace("Edg/", "Edge ").replace("OPR/", "Opera ").replace("/", " ");
  const device = /iPhone/.test(ua)
    ? "iPhone"
    : /iPad/.test(ua)
      ? "iPad"
      : /Android/.test(ua)
        ? "Android phone"
        : /Macintosh/.test(ua)
          ? "Mac"
          : /Windows/.test(ua)
            ? "Windows PC"
            : /Linux/.test(ua)
              ? "Linux PC"
              : "Unknown device";
  return { device, browser: browser || "Browser" };
}

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function ProfileSessions() {
  const [sessions, setSessions] = useState(null);
  const [logs, setLogs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/api/auth/account-activity");
      setSessions(data.sessions || []);
    } catch (e) {
      setSessions((s) => s || []);
      setMsg(getErrorMessage(e));
    }
  };

  useEffect(() => {
    load();
    api
      .get("/api/user/audit")
      .then((r) => setLogs(r.data || []))
      .catch(() => setLogs([]));
  }, []);

  const logoutOthers = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/api/auth/sessions/logout-others");
      setMsg(`Logged out ${data.removed} other session${data.removed === 1 ? "" : "s"}.`);
      load();
    } catch (e) {
      setMsg(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/api/user/sessions/${id}/revoke`);
      load();
    } catch (e) {
      setMsg(getErrorMessage(e));
    }
  };

  return (
    <AppShell>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Activity sessions</h1>
            <p className="mt-1 text-sm text-muted-foreground">Anything you don&apos;t recognise here should be logged out now.</p>
          </div>
          <button
            type="button"
            onClick={logoutOthers}
            disabled={busy || (sessions || []).length < 2}
            data-testid="logout-others-btn"
            className="rounded-lg border border-border bg-surface-2 px-3.5 py-2 text-sm transition hover:border-down hover:text-down disabled:opacity-50"
          >
            Log out of all other sessions
          </button>
        </div>

        {msg && <p className="text-xs text-muted-foreground">{msg}</p>}

        <div className="overflow-hidden rounded-xl border border-border bg-surface/40" data-testid="sessions-list">
          {sessions === null ? (
            <div className="space-y-2 p-4">
              <div className="skeleton h-10 w-full" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">No active sessions.</p>
          ) : (
            sessions.map((s) => {
              const { device, browser } = parseAgent(s.user_agent);
              return (
                <div key={s.id} className="terminal-row grid-cols-[1.4fr_1fr_1fr_130px] gap-3 px-4 py-4">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-medium">
                      {device}
                      {s.is_current && <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] text-primary">This device</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{browser}</p>
                  </div>
                  <div className="text-sm text-muted-foreground">Since {when(s.created_at)}</div>
                  <div className="num text-sm text-muted-foreground">{s.ip || "—"}</div>
                  <div className="flex items-center justify-end gap-3">
                    {s.is_current ? (
                      <span className="num text-xs text-muted-foreground">Active now</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => revoke(s.id)}
                        className="rounded-md border border-border px-2.5 py-1 text-xs transition hover:border-down hover:text-down"
                      >
                        Log out
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="pt-4">
          <h2 className="text-lg font-semibold">Security log</h2>
          <p className="mt-1 text-sm text-muted-foreground">Sign-ins and security events, retained per FIU-IND rules.</p>
          <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-surface/40">
            <div className="min-w-[600px]">
              <div className="table-head grid grid-cols-[1.6fr_90px_1fr_130px] gap-3 px-4 py-2.5">
                <span>Event</span>
                <span>Status</span>
                <span>IP</span>
                <span className="text-right">Time</span>
              </div>
              {logs === null ? (
                <div className="space-y-2 p-4">
                  <div className="skeleton h-8 w-full" />
                </div>
              ) : logs.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">No activity yet.</p>
              ) : (
                logs.map((l, i) => (
                  <div key={i} data-testid={`activity-row-${i}`} className="terminal-row grid-cols-[1.6fr_90px_1fr_130px] gap-3 px-4 py-3 text-sm">
                    <span className="truncate capitalize">{String(l.event || "").replace(/_/g, " ")}</span>
                    <span className={`text-xs ${l.ok ? "text-up" : "text-down"}`}>{l.ok ? "Success" : "Failed"}</span>
                    <span className="num text-muted-foreground">{l.ip || "—"}</span>
                    <span className="num text-right text-xs text-muted-foreground">{when(l.created_at)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
